import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Loader2, Menu, Pencil, Trash2, Check, ChevronDown, MessageSquare } from "lucide-react";

function initials(name) {
  return name ? name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() : "?";
}
function ago(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "less than a minute ago";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return new Date(iso).toLocaleDateString("en-GB");
}
function Tag({ children }) {
  return <span className="text-xs font-medium text-indigo-700 bg-indigo-50 rounded-full px-2.5 py-1">{children}</span>;
}

export default function TaskComments({ legacyId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [showOnApp, setShowOnApp] = useState(true);
  const [internal, setInternal] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/tasks/${legacyId}/comments`).then((r) => { setItems(r.data.items || []); setMe(r.data.me); }).catch(() => setItems([]));
  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, [legacyId]);

  const post = async () => {
    if (!body.trim()) { toast.error("Comment cannot be empty"); return; }
    setBusy(true);
    try {
      if (editing) {
        const r = await api.patch(`/tasks/${legacyId}/comments/${editing}`, { body, show_on_app: showOnApp, internal });
        setItems((s) => s.map((c) => (c.id === editing ? r.data.comment : c)));
        setEditing(null);
      } else {
        const r = await api.post(`/tasks/${legacyId}/comments`, { body, show_on_app: showOnApp, internal });
        setItems((s) => [...s, r.data.comment]);
      }
      setBody("");
      toast.success("Comment posted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post comment"); }
    finally { setBusy(false); }
  };

  const startEdit = (c) => { setEditing(c.id); setBody(c.body); setShowOnApp(c.show_on_app); setInternal(c.internal); };
  const remove = async (c) => {
    try { await api.delete(`/tasks/${legacyId}/comments/${c.id}`); setItems((s) => s.filter((x) => x.id !== c.id)); toast.success("Comment deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete comment"); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="task-comments">
      <div className="flex items-center gap-2 px-5 py-3.5 bg-slate-50/70 border-b border-slate-100">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left text-base font-bold text-slate-900" data-testid="task-comments-toggle">
          <MessageSquare className="w-4 h-4 text-indigo-500" /> Comments
          <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold" data-testid="task-comments-count">{items.length}</span>
        </button>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-slate-400 hover:text-slate-600" aria-label="Toggle Comments">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
      </div>

      {open && (<div className="px-5 py-5">
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-4 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="divide-y divide-slate-100 mb-4">
          {items.length === 0 && <div className="text-center text-sm text-slate-400 py-4">No comments yet.</div>}
          {items.map((c) => (
            <div key={c.id} className="py-3 flex gap-3" data-testid={`task-comment-${c.id}`}>
              <span className="w-9 h-9 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center shrink-0">{initials(c.author_name)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900">{c.author_name}</span>
                  <span className="text-xs text-slate-400">{ago(c.created_at)}{c.updated_at ? " · edited" : ""}</span>
                  <div className="ml-auto flex items-center gap-1.5">
                    {c.internal && <Tag>Internal</Tag>}
                    {c.show_on_app && <Tag>App</Tag>}
                    {(c.author_id === me) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid={`task-comment-menu-${c.id}`}><Menu className="w-4 h-4" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => startEdit(c)} data-testid={`task-comment-edit-${c.id}`}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => remove(c)} className="text-rose-600" data-testid={`task-comment-del-${c.id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem disabled className="opacity-100 justify-between"><span>Visible on app</span>{c.show_on_app && <Check className="w-4 h-4 text-emerald-600" />}</DropdownMenuItem>
                          <DropdownMenuItem disabled className="opacity-100 justify-between"><span>Internal</span>{c.internal && <Check className="w-4 h-4 text-emerald-600" />}</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
                <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words">{c.body}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 p-3">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Type comment here" data-testid="task-comment-input"
          className="w-full px-3 py-2 text-sm rounded-lg bg-slate-50 border border-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 resize-y" />
        <div className="flex flex-wrap items-center justify-end gap-4 mt-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">Show On App <Switch checked={showOnApp} onCheckedChange={setShowOnApp} data-testid="task-comment-showapp" /></label>
          <label className="flex items-center gap-2 text-sm text-slate-600">Internal <Switch checked={internal} onCheckedChange={setInternal} data-testid="task-comment-internal" /></label>
          {editing && <Button variant="outline" size="sm" onClick={() => { setEditing(null); setBody(""); }}>Cancel</Button>}
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={post} data-testid="task-comment-post">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}{editing ? "Save" : "Post"}
          </Button>
        </div>
      </div>
      </div>)}
    </section>
  );
}
